import { Button, Tooltip } from 'antd';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BiMessageSquareDetail } from 'react-icons/bi';
import { FaSearch } from 'react-icons/fa';
import { GiMoneyStack } from 'react-icons/gi';
import { GrFavorite } from 'react-icons/gr';
import { IoDiamond } from 'react-icons/io5';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../../assets/fala-480.png';
import supabase from '../../config/supabaseConfig';
import ModalOpener from '../../features/auth/components/ModalOpener';
import PromotionModal from '../../features/home/components/PromotionModal';
import { useAuth } from '../../Hooks/useAuth';
import { useUnreadCount } from '../../Hooks/useUnreadCount';
import { UserProfile } from '../../types/userTypes';
import CategoriesDropdown from '../categories_list/CategoriesDropdown';
import BtnLinear from './BtnLinearBgHook';
import UserProfileModal from './UserProfileModal';

const MainNav = () => {
    const navigate = useNavigate();
    const { user, signOut } = useAuth();
    const [userData, setUserData] = useState<UserProfile | null>(null);
    const totalUnread = useUnreadCount();
    const [searchQuery, setSearchQuery] = useState<string>("");
    const headerRef = useRef(null);
    const [isSticky, setIsSticky] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

    // Fetch user profile data from Supabase
    useEffect(() => {
        if (user) {
            const getUserData = async () => {
                try {
                    // Don't use .single() to avoid the error when no profile exists
                    const { data, error } = await supabase
                        .from('profiles')
                        .select('*')
                        .eq('id', user.id);

                    if (error) throw error;
                    if (data && data.length > 0) {
                        setUserData({
                            id: user.id,
                            name: data[0].name,
                            email: data[0].email,
                            phone: data[0].phone,
                            photo_url: data[0].photo_url,
                            is_verified: data[0].is_verified,
                            is_online: data[0].is_online,
                            is_admin: data[0].is_admin,
                            created_at: new Date(data[0].created_at)
                        });
                    }
                } catch {
                    /* Removed console statement */
                }
            };
            getUserData();
        }
    }, [user]);

    const debouncedSearch = useCallback((query: string) => {
        if (query.trim()) {
            navigate(`/search?query=${encodeURIComponent(query)}`);
        }
    }, [navigate]);

    const debouncedSearchWithDelay = debounce(debouncedSearch, 500);

    const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setSearchQuery(query);
        debouncedSearchWithDelay(query);
    };

    const onLogoutClick = async () => {
        try {
            await signOut();
            navigate("/auth/login");
        } catch {
            /* Removed console statement */
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 20) {
                setIsSticky(true);
            } else {
                setIsSticky(false);
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Check if the user is an admin
    const isAdmin = userData?.is_admin || false;

    return isAdmin ? null : (
        <div>
            <header
                ref={headerRef}
                className={`bg-light-default transition-all duration-300 ease-in-out ${isSticky ? 'fixed top-0 left-0 right-0 z-50 shadow-lg' : ''}`}
            >
                <div className="mx-auto px-8 py-2 flex justify-between items-center">
                    <div className="pl-12">
                        <Link to="/">
                            <img className="h-8 md:h-10" src={logo} alt="Logo" />
                        </Link>
                    </div>

                    <form className="z-50 flex-grow w-full max-w-lg xl:max-w-lg 2xl:max-w-2xl bg-surface-tertiary rounded-md flex items-center space-x-2 px-2 shadow-md">
                        <CategoriesDropdown />

                        <input
                            className="flex-grow border-none bg-transparent font-semibold text-sm placeholder-content-tertiary focus:outline-none px-2"
                            type="text"
                            placeholder={'rechercher un produit ...'}
                            value={searchQuery}
                            onChange={handleSearchInputChange}
                        />

                        <button
                            aria-label='rechercher'
                            type="submit"
                            className="primary-gradient-text"
                        >
                            <FaSearch className="h-5 w-5 text-primary-default" />
                        </button>
                    </form>

                    {/* min-w-max: on narrower screens this side keeps its buttons on one line
                        and the search bar shrinks instead */}
                    <div className='flex w-1/3 min-w-max items-center justify-end gap-3'>
                        {/* Plans & premium popup. Text from xl up, icon only on smaller laptops */}
                        <Tooltip title="Plans VIP & annonces premium">
                            <button
                                type="button"
                                onClick={() => setIsModalVisible(true)}
                                aria-label="Plans VIP"
                                className="flex items-center gap-1.5 h-10 px-3 xl:px-4 rounded-lg font-bold text-sm text-white bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 shadow-sm hover:shadow-md transition-all"
                            >
                                <IoDiamond size={18} />
                                <span className="hidden xl:inline">VIP</span>
                            </button>
                        </Tooltip>

                        {/* Straight to the sell page */}
                        <BtnLinear>
                            <Button
                                type="primary"
                                size='large'
                                autoInsertSpace={false}
                                icon={<GiMoneyStack size={25} />}
                                iconPosition='end'
                                onClick={() => navigate('/sell')}
                            >
                                Vendre
                            </Button>
                        </BtnLinear>

                        {user ? (
                            <nav className="contents">
                                <ul className="ml-1 flex items-center justify-end">
                                    <li className="mr-2 lg:mr-5 relative inline-block">
                                        <Link to="/chats">
                                            {totalUnread > 0 && (
                                                <div className="absolute -top-0.5 -right-1 z-10 text-primary-contrast bg-accent-default text-xs font-bold px-1.5 py-[1px] rounded-full">
                                                    {totalUnread}
                                                </div>
                                            )}

                                            <div className="flex items-center flex-col justify-center text-primary-default">
                                                <div className="p-1 rounded-full bg-primary-secondary">
                                                    <div className="bg-light-default p-1 rounded-full">
                                                        <BiMessageSquareDetail size={20} className="text-primary-default" />
                                                    </div>
                                                </div>
                                                <p className="text-sm font-semibold text-primary-default">
                                                    Messages
                                                </p>
                                            </div>
                                        </Link>
                                    </li>
                                    <li className="mr-2 lg:mr-5 relative inline-block">
                                        <Link to="/favorites">
                                            <div className="flex items-center flex-col justify-center text-primary-default">
                                                <div className="p-1 rounded-full bg-primary-secondary">
                                                    <div className="bg-light-default p-1 rounded-full">
                                                        <GrFavorite size={20} className="text-primary-default" />
                                                    </div>
                                                </div>
                                                <p className="text-sm font-semibold text-primary-default">
                                                    Favoris
                                                </p>
                                            </div>
                                        </Link>
                                    </li>

                                    <li className="mr-2 lg:mr-5 relative inline-block">
                                        {userData && (
                                            <UserProfileModal
                                                user={userData}
                                                onLogout={onLogoutClick}
                                            />
                                        )}
                                    </li>
                                </ul>
                            </nav>
                        ) : (
                            <nav>
                                <ul className="ml-1 flex items-center justify-end">
                                    <li><ModalOpener /></li>
                                </ul>
                            </nav>
                        )}
                    </div>
                </div>
                <PromotionModal
                    visible={isModalVisible}
                    onClose={() => setIsModalVisible(false)}
                    userId={user?.id}
                />
            </header>
        </div>
    );
};

export default MainNav;
